<template>
  <div class="floor-plan-canvas">
    <img
      ref="floorPlanImage"
      :src="floorPlan.image_url"
      :alt="floorPlan.name"
      class="floor-plan-image"
      crossorigin="anonymous"
      @load="onImageLoad"
    />

    <!-- 全アイテム未配置時のヒントメッセージ -->
    <div v-if="hasNoPlacedItems" class="placement-hint-overlay">
      <v-card class="placement-hint-card" elevation="3">
        <v-card-text class="text-center">
          <v-icon large class="mb-2">mdi-information-outline</v-icon>
          <div class="text-h6 mb-2">{{ $t('placement_hint.title') }}</div>
          <div class="text-body-2">{{ $mpt($t('placement_hint.description')) }}</div>
        </v-card-text>
      </v-card>
    </div>

    <!-- 配置モードバナー -->
    <div v-if="capturePlacementMode" class="placement-mode-banner">
      <div class="placement-mode-banner__main">
        <v-icon :size="20" class="placement-mode-banner__icon">touch_app</v-icon>
        <div class="placement-mode-banner__title">
          {{ $t('placement_mode.message') }}
        </div>
      </div>
    </div>

    <svg
      ref="svg"
      :viewBox="viewBox"
      :class="['floor-plan-svg', { 'floor-plan-svg--dragging': isDragging }]"
      data-testid="floor-plan-svg"
      role="img"
      :aria-label="$t('floor_plan_canvas')"
      @dragover.prevent
      @drop="handleDrop"
      @mousemove="handleMouseMove"
      @mouseup="handleMouseUp"
      @mouseleave="handleMouseUp"
    >
      <g
        v-for="item in placedItems"
        :key="item.itemId"
        class="item-group"
        :data-testid="`item-group-${item.itemId}`"
        :opacity="item.status === 'inactive' ? INACTIVE_OPACITY : 1"
        @mouseenter="handleItemMouseEnter($event, item)"
        @mouseleave="handleItemMouseLeave"
      >
        <bubble-circle
          v-if="isCircleVisible"
          :cx="item.coordinates.x"
          :cy="item.coordinates.y"
          :count="item.count"
          :min-radius="minCircleRadius"
          :max-radius="maxCircleRadius"
          :circle-scale-max="circleScaleMax"
          data-testid="bubble-circle"
          :aria-label="$t('bubble_label', { label: item.label, count: item.count })"
          @mousedown="handleMouseDown($event, item.itemId)"
        />

        <arrow-marker
          :x="item.coordinates.x"
          :y="item.coordinates.y"
          :rotation="item.coordinates.rotation || 0"
          :marker-size="markerSize"
          :size-scale="sizeScale"
          :color="item.color"
          :rotating="rotatingItem === item.itemId"
          :is-exporting="isExporting"
          :aria-label="$t('marker_label', { label: item.label })"
          @mousedown="handleMouseDown($event, item.itemId)"
          @rotation-start="startRotation($event, item)"
        />

        <marker-label
          :x="labelX(item)"
          :y="item.coordinates.y"
          :label="item.label"
          :count="item.count"
          :size-scale="sizeScale"
          :show-name="isLabelVisible"
          :show-count="isCountVisible"
        />
      </g>

      <!-- 捕獲記録ピン -->
      <g
        v-for="captureItem in placedCaptureItems"
        :key="'capture-' + captureItem.id"
        data-testid="capture-pin"
        :transform="'translate(' + captureItem.coordinates.x + ',' + captureItem.coordinates.y + ')'"
        @mousedown="handleCaptureMouseDown($event, captureItem)"
        @click="handleCaptureClick(captureItem)"
      >
        <title>{{ captureTooltipText(captureItem) }}</title>
        <g class="capture-pin-inner">
          <!-- 紫色ベース円 -->
          <circle
            :r="pinRadius"
            class="capture-pin-circle"
            fill="#7b1fa2"
            stroke="#333"
            stroke-width="2"
            data-testid="capture-pin-circle"
          />
          <!-- pest_control_rodent アイコン（SVGパス直接描画） -->
          <svg
            :x="-iconHalfSize" :y="-iconHalfSize"
            :width="iconSize" :height="iconSize"
            viewBox="0 -960 960 960"
          >
            <path :d="rodentIconPath" fill="#fff" />
          </svg>
          <!-- 捕獲数バッジ -->
          <circle
            :cx="badgeOffset" :cy="-badgeOffset"
            :r="badgeRadius"
            class="capture-pin-badge"
            fill="#e53935"
            stroke="#fff"
            stroke-width="1.5"
            data-testid="capture-pin-badge"
          />
          <text
            :x="badgeOffset" :y="-badgeOffset"
            text-anchor="middle" dominant-baseline="central"
            :font-size="badgeFontSize"
            fill="#fff" font-weight="700" font-family="sans-serif"
          >{{ captureItem.count }}</text>
        </g>
      </g>

      <!-- 配置モードオーバーレイ -->
      <rect
        v-if="capturePlacementMode"
        :width="imageDimensions.width"
        :height="imageDimensions.height"
        fill="transparent"
        style="cursor: crosshair;"
        data-testid="capture-placement-overlay"
        @click="handlePlacementClick"
      />

      <export-info
        :place-name="placeName"
        :floor-plan-name="floorPlan.name"
        :selected-month="selectedMonth"
        :detection-filter="detectionFilter"
        :size-scale="sizeScale"
      />
    </svg>
    <!-- 凡例は SVG の外に置く。SVG 内に入れるとマーカーと重なった際に
         下のマーカーを操作できなくなるため。画像の書き出し対象には含める -->
    <div class="floor-plan-legend">
      <sensor-type-legend :sensors="sensors" :is-exporting="isExporting" />
    </div>
    <histogram-popup
      v-model="showPopup"
      :histogram-data="hoveredItemHistogramData"
      :item-label="hoveredItem ? hoveredItem.label : ''"
      :position-x="popupPosition.x"
      :position-y="popupPosition.y"
    />
  </div>
</template>

<i18n lang="yaml">
ja:
  floor_plan_canvas: 図面キャンバス
  bubble_label: "{label} 検出数:{count}"
  marker_label: "{label} マーカー"
  countUnit: 頭
  species:
    brown_rat: クマネズミ
    black_rat: ドブネズミ
    house_mouse: ハツカネズミ
    other: その他
    unknown: 不明
  placement_hint:
    title: アイテムを配置しましょう
    description: >
      右側のリストからセンサーまたは捕獲記録をドラッグして
      図面上にドロップしてください
  placement_mode:
    message: 図面をクリックして捕獲場所を指定してください（ESCでキャンセル）
en:
  floor_plan_canvas: Floor Plan Canvas
  bubble_label: "{label} Count:{count}"
  marker_label: "{label} marker"
  countUnit: ""
  species:
    brown_rat: Brown Rat
    black_rat: Black Rat
    house_mouse: House Mouse
    other: Other
    unknown: Unknown
  placement_hint:
    title: Place items on the floor plan
    description: >
      Drag sensors or capture records from the list on the right
      and drop them onto the floor plan
  placement_mode:
    message: Click on the floor plan to specify the capture location (ESC to cancel)
</i18n>

<script>
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';
import { screenToSvgCoordinates } from '@/libs/floorPlanCoordinates';
import { DISPLAY_MODES } from './displayModes';
import { INACTIVE_OPACITY } from './floorPlanItemDisplay';
import { aggregateHourlyDetections } from './hourlyHistogram';
import BubbleCircle from './BubbleCircle';
import MarkerLabel from './MarkerLabel';
import ArrowMarker from './ArrowMarker';
import ExportInfo from './ExportInfo';
import HistogramPopup from './HistogramPopup';
import SensorTypeLegend from './SensorTypeLegend';

// pest_control_rodent アイコンのSVGパスデータ（Material Symbols Rounded, FILL=1）
// viewBox: 0 -960 960 960
// eslint-disable-next-line max-len
const RODENT_ICON_PATH = 'M480-120q-90 0-158-57.5T243-324q-72-14-117.5-70T80-522q0-83 58.5-140.5T280-720h100q8 0 14-6t6-14q0-8-6-14t-14-6h-60q-17 0-28.5-11.5T280-800q0-17 11.5-28.5T320-840h60q42 0 71 29t29 71q0 42-29 71t-71 29H280q-50 0-85 34t-35 83q0 40 23 72.5t61 44.5q16-85 81.5-139.5T478-600q20 0 38.5 2.5T554-589q-51 21-82.5 66T440-423q0 32 9 62.5t29 53.5q5 6 13.5 6.5T506-306q5-5 5.5-13.5T507-334q-14-18-20.5-40t-6.5-46q0-59 41-99.5T621-560q58 0 98.5 40.5T760-422q0 24-8.5 46.5T726-336l126 110q12 11 19.5 24.5T879-172q0 23-19.5 37.5T814-120H480Zm200-80q17 0 28.5-11.5T720-240q0-17-11.5-28.5T680-280q-17 0-28.5 11.5T640-240q0 17 11.5 28.5T680-200Z';

const CAPTURE_PIN_RADIUS = 20;
const CAPTURE_ICON_SIZE = 22;
const CAPTURE_BADGE_RADIUS = 9;
const CAPTURE_BADGE_OFFSET = 14;

const HOVER_DELAY_MS = 750;
const BASE_IMAGE_SIZE = 1000;
const MARKER_SIZE_AT_BASE = 26;
const MIN_CIRCLE_RADIUS_AT_BASE = 16;
const MAX_CIRCLE_RADIUS_AT_BASE = 60;
const FALLBACK_CIRCLE_SCALE = 1000;
const DEFAULT_VIEWBOX = '0 0 1000 1000';

export default {
  name: 'FloorPlanCanvas',
  components: {
    BubbleCircle,
    MarkerLabel,
    ArrowMarker,
    ExportInfo,
    HistogramPopup,
    SensorTypeLegend,
  },
  props: {
    floorPlan: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    sensors: {
      type: Array,
      default: () => [],
    },
    captureItems: {
      type: Array,
      default: () => [],
    },
    capturePlacementMode: {
      type: Boolean,
      default: false,
    },
    displayMode: {
      type: String,
      default: 'none',
      validator: (value) => Object.values(DISPLAY_MODES).includes(value),
    },
    placeName: {
      type: String,
      default: '',
    },
    selectedMonth: {
      type: String,
      default: '',
    },
    detectionFilter: {
      type: String,
      default: '',
    },
    sensorEvents: {
      type: Object,
      default: () => ({}),
    },
    isExporting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      INACTIVE_OPACITY,
      viewBox: DEFAULT_VIEWBOX,
      imageDimensions: { width: 0, height: 0, minDimension: 0 },
      draggingItem: null,
      draggingCaptureItem: null,
      rotatingItem: null,
      markerSize: MARKER_SIZE_AT_BASE,
      hoveredItem: null,
      hoverTimer: null,
      popupPosition: { x: 0, y: 0 },
      rodentIconPath: RODENT_ICON_PATH,
      captureGrabOffset: { dx: 0, dy: 0 },
      captureDidDrag: false,
      justDraggedCapture: false,
    };
  },
  computed: {
    hasNoPlacedItems() {
      return !this.items.some(item => item.isPlaced)
        && !this.captureItems.some(item => item.coordinates != null);
    },
    isDragging() {
      return this.draggingItem !== null
        || this.draggingCaptureItem !== null
        || this.rotatingItem !== null;
    },
    placedItems() {
      return this.items.filter(item => item.isPlaced);
    },
    placedCaptureItems() {
      return this.captureItems.filter(item => item.coordinates != null);
    },
    sizeScale() {
      return this.imageDimensions.minDimension > 0
        ? this.imageDimensions.minDimension / BASE_IMAGE_SIZE : 1.0;
    },
    minCircleRadius() {
      return MIN_CIRCLE_RADIUS_AT_BASE * this.sizeScale;
    },
    maxCircleRadius() {
      return MAX_CIRCLE_RADIUS_AT_BASE * this.sizeScale;
    },
    circleScaleMax() {
      return this.floorPlan.circle_scale_max ?? FALLBACK_CIRCLE_SCALE;
    },
    isLabelVisible() {
      return this.displayMode === DISPLAY_MODES.NAME
        || this.displayMode === DISPLAY_MODES.NAME_AND_COUNT;
    },
    isCountVisible() {
      return this.displayMode === DISPLAY_MODES.NAME_AND_COUNT;
    },
    isCircleVisible() {
      return this.displayMode !== DISPLAY_MODES.PLACEMENT_ONLY;
    },
    pinRadius() {
      return CAPTURE_PIN_RADIUS * this.sizeScale;
    },
    iconSize() {
      return CAPTURE_ICON_SIZE * this.sizeScale;
    },
    iconHalfSize() {
      return (CAPTURE_ICON_SIZE / 2) * this.sizeScale;
    },
    badgeRadius() {
      return CAPTURE_BADGE_RADIUS * this.sizeScale;
    },
    badgeOffset() {
      return CAPTURE_BADGE_OFFSET * this.sizeScale;
    },
    badgeFontSize() {
      return 10 * this.sizeScale;
    },
    showPopup: {
      get() {
        return this.hoveredItem != null;
      },
      set(value) {
        if (!value) {
          this.hoveredItem = null;
        }
      },
    },
    hoveredItemHistogramData() {
      if (this.hoveredItem == null) return new Array(24).fill(0);
      const events = this.sensorEvents[this.hoveredItem.itemId] || [];
      return aggregateHourlyDetections(events, this.detectionFilter);
    },
  },
  beforeDestroy() {
    this.clearHoverTimer();
  },
  methods: {
    onImageLoad() {
      const img = this.$refs.floorPlanImage;
      const { naturalWidth, naturalHeight } = img;
      this.imageDimensions = {
        width: naturalWidth,
        height: naturalHeight,
        minDimension: Math.min(naturalWidth, naturalHeight),
      };
      this.viewBox = `0 0 ${naturalWidth} ${naturalHeight}`;
      this.markerSize = MARKER_SIZE_AT_BASE * this.sizeScale;
    },
    handleDrop(event) {
      event.preventDefault();
      const { x, y } = this.getCoordinatesFromEvent(event);

      // 1. センサー（既存形式: getData('itemId') で生文字列）
      const itemId = event.dataTransfer.getData('itemId');
      if (itemId) {
        this.$emit('change', { itemId, coordinates: { x, y, rotation: 0 } });
        return;
      }

      // 2. 捕獲記録（JSON形式: getData('text/plain')）
      const raw = event.dataTransfer.getData('text/plain');
      if (!raw) return;
      try {
        const data = JSON.parse(raw);
        if (data.type === 'capture') {
          this.$emit('capture-change', { id: data.id, coordinates: { x, y } });
        }
      } catch {
        // JSON解析失敗は無視
      }
    },
    handleMouseDown(event, itemId) {
      this.clearHoverTimer();
      this.hoveredItem = null;
      event.preventDefault();
      this.draggingItem = itemId;
    },
    handleMouseMove(event) {
      if (this.draggingItem) {
        const { x, y } = this.getCoordinatesFromEvent(event);
        const item = this.items.find(i => i.itemId === this.draggingItem);
        const rotation = item?.coordinates?.rotation || 0;
        this.$emit('change', { itemId: this.draggingItem, coordinates: { x, y, rotation } });
      } else if (this.draggingCaptureItem) {
        this.captureDidDrag = true;
        const cursor = this.getCoordinatesFromEvent(event);
        this.$emit('capture-change', {
          id: this.draggingCaptureItem,
          coordinates: {
            x: cursor.x - this.captureGrabOffset.dx,
            y: cursor.y - this.captureGrabOffset.dy,
          },
        });
      } else if (this.rotatingItem) {
        this.handleRotationMove(event);
      }
    },
    handleMouseUp() {
      if (this.draggingCaptureItem && this.captureDidDrag) {
        this.justDraggedCapture = true;
        setTimeout(() => { this.justDraggedCapture = false; }, 0);
      }
      this.draggingItem = null;
      this.draggingCaptureItem = null;
      this.rotatingItem = null;
    },
    startRotation(event, item) {
      event.preventDefault();
      event.stopPropagation();
      this.clearHoverTimer();
      this.hoveredItem = null;
      this.rotatingItem = item.itemId;
    },
    handleRotationMove(event) {
      const item = this.items.find(i => i.itemId === this.rotatingItem);
      if (!item) return;
      const { x, y } = this.getCoordinatesFromEvent(event);
      const dx = x - item.coordinates.x;
      const dy = y - item.coordinates.y;
      const angle = ((Math.atan2(dx, -dy) * 180) / Math.PI + 360) % 360;
      this.$emit('change', {
        itemId: this.rotatingItem,
        coordinates: { x: item.coordinates.x, y: item.coordinates.y, rotation: Math.round(angle) },
      });
    },
    handleItemMouseEnter(event, item) {
      if (this.isDragging) return;
      this.clearHoverTimer();
      this.hoverTimer = setTimeout(() => {
        this.hoveredItem = item;
        this.popupPosition = {
          x: event.clientX,
          y: event.clientY,
        };
      }, HOVER_DELAY_MS);
    },
    handleItemMouseLeave() {
      this.clearHoverTimer();
      this.hoveredItem = null;
    },
    clearHoverTimer() {
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer);
        this.hoverTimer = null;
      }
    },
    handleCaptureClick(captureItem) {
      if (this.isDragging || this.justDraggedCapture) return;
      this.$emit('capture-click', captureItem);
    },
    captureTooltipText(captureItem) {
      return `${this.$t(`species.${captureItem.species}`)} ${captureItem.count}${this.$t('countUnit')}`;
    },
    handleCaptureMouseDown(event, captureItem) {
      event.preventDefault();
      event.stopPropagation();
      this.draggingCaptureItem = captureItem.id;
      this.captureDidDrag = false;
      const cursor = this.getCoordinatesFromEvent(event);
      this.captureGrabOffset = {
        dx: cursor.x - captureItem.coordinates.x,
        dy: cursor.y - captureItem.coordinates.y,
      };
    },
    handlePlacementClick(event) {
      if (!this.capturePlacementMode) return;
      const coordinates = this.getCoordinatesFromEvent(event);
      this.$emit('capture-place', { coordinates });
    },
    getCoordinatesFromEvent(event) {
      const { svg } = this.$refs;
      const rect = svg.getBoundingClientRect();
      return screenToSvgCoordinates(event.clientX, event.clientY, rect, this.imageDimensions);
    },
    labelX(item) {
      return item.coordinates.x + this.markerSize / 2 + 4 * this.sizeScale;
    },
    async exportImage() {
      const dataUrl = await toPng(this.$el, {
        cacheBust: false,
        skipFonts: true,
      });
      const blob = await fetch(dataUrl).then((res) => res.blob());
      const fileName = `${this.floorPlan.name}_${this.selectedMonth}.png`;
      saveAs(blob, fileName);
    },
  },
};
</script>

<style scoped>
.floor-plan-canvas {
  position: relative;
  width: 100%;
  max-width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}

.placement-hint-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  pointer-events: none;
}

.placement-hint-card {
  max-width: 400px;
  opacity: 0.95;
}

.floor-plan-image {
  width: 100%;
  display: block;
}

.floor-plan-legend {
  position: absolute;
  top: 4px;
  right: 4px;
  /* 図面の余白が狭い場合にマーカーへ重なっても、下のマーカーを操作できるようにする */
  pointer-events: none;
  z-index: 5;
}

/* 注記アイコンはツールチップ操作が必要なため、ここだけ操作を受け取る */
.floor-plan-legend ::v-deep .v-icon {
  pointer-events: auto;
}

.floor-plan-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.floor-plan-svg--dragging,
.floor-plan-svg--dragging ::v-deep * {
  cursor: none !important;
}

.capture-pin-inner {
  cursor: pointer;
  transition: transform 0.15s;
  transform-box: fill-box;
  transform-origin: center;
}

.capture-pin-inner:hover {
  transform: scale(1.15);
}

.capture-pin-inner:hover .capture-pin-circle {
  fill: #9c27b0;
}

.placement-mode-banner {
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: #fff8e8;
  border: 1px solid #ecd9aa;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(82, 60, 12, 0.08);
  z-index: 11; /* placement-hint-overlay(z:10)より上 */
  pointer-events: none;
}

.placement-mode-banner__main {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.placement-mode-banner__icon {
  margin-top: 1px;
}

.placement-mode-banner__title {
  font-size: 13px;
  font-weight: 600;
  color: #5f4300;
}
</style>
